import { useNavigate } from 'react-router';

import styled from 'styled-components';

import { MembershipPlansSummary } from 'apps-common/components/MembershipPlansSummary';
import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { isStrictNever } from 'apps-common/utils/isStrictNever';
import { t } from 'translations';
import { Button, Header, Loader } from 'ui';
import { MainContainer } from 'ui/styles/containers';
import { HeaderText, Paragraph } from 'ui/styles/text';
import { media } from 'ui/themes/signup';

import { routes } from '../routes';

const Container = styled(MainContainer)`
  align-self: center;
  flex-grow: 1;
  width: 45rem;
  gap: 30px;

  ${media.medium`
    margin: 5vh 0;
    text-align: center;
    max-height: 45vh;
  `}

  h1 {
    text-wrap: balance;
  }

  p {
    margin: 0;
  }

  button {
    margin-top: auto;
  }
`;

export const successPageTypes = ['update', 'renew', 'payment-method'] as const;
export type SuccessPageTypes = (typeof successPageTypes)[number];

const Info = ({ type }: { type: SuccessPageTypes }) => {
  switch (type) {
    case 'update':
      return (
        <HeaderText data-testid="update" $fontSize="xxxlarge" $margin="12px 0">
          {t('membership_hub_membership_update_success')}
        </HeaderText>
      );
    case 'renew':
      return (
        <div>
          <HeaderText data-testid="renew" $fontSize="xxxlarge" $margin="12px 0">
            {t('membership_hub_welcome_back')}
          </HeaderText>
          <Paragraph $color="grayLightest" $margin="0 0 30px 0">
            {t('membership_hub_renew_notification_body')}
          </Paragraph>
        </div>
      );
    case 'payment-method':
      return (
        <div>
          <HeaderText data-testid="payment-method" $fontSize="xxxlarge" $margin="12px 0">
            {t('membership_hub_payment_method_update_success')}
          </HeaderText>
          <Paragraph $color="grayLightest" $margin="0 0 30px 0">
            {t('membership_hub_payment_method_update_success_body_text')}
          </Paragraph>
        </div>
      );
    default:
      return isStrictNever(type);
  }
};

export const SuccessPage = ({ type }: { type: SuccessPageTypes }) => {
  const navigate = useNavigate();

  const { data, isFetching } = useGetAccount();
  const { membership, b2bPartnerName = null } = data?.account ?? {};

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      <Header appType="hub" />
      <Container>
        <Info type={type} />

        {membership && <MembershipPlansSummary membership={membership} b2bPartnerName={b2bPartnerName} />}

        <Button
          data-testid="return-home"
          onClick={() => {
            void navigate(routes.index);
          }}
        >
          {t('membership_hub_return_to_hub_home')}
        </Button>
      </Container>
    </>
  );
};
